import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RolesContext } from '../../contexts/RolesContext'
import { AppContainer, AppContainerImage, AppContainerVideo } from './styled'

// ─── Animation config ───────────────────────────────────────────────────────

const ANIM_KEY = 'softys_animations';

interface AnimConfig {
  typewriter: boolean;
  logoReveal: boolean;
  qrPulse: boolean;
  textEffects: boolean;
}

const DEFAULT_ANIM: AnimConfig = {
  typewriter: true,
  logoReveal: true,
  qrPulse: true,
  textEffects: true,
};

function loadAnimConfig(): AnimConfig {
  try { return { ...DEFAULT_ANIM, ...JSON.parse(localStorage.getItem(ANIM_KEY) || '{}') }; }
  catch { return DEFAULT_ANIM; }
}

const WELCOME_TEXT = 'Você já conhece a fralda que mais cresce no Brasil?';

// CSS keyframes inserted once into <head>
const KEYFRAMES_CSS = `
@keyframes blink-cursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
@keyframes logo-reveal {
  0%   { clip-path: inset(0 100% 0 0); opacity: 0; }
  15%  { opacity: 1; }
  100% { clip-path: inset(0 0% 0 0); opacity: 1; }
}
@keyframes qr-shake {
  0%,  100% { transform: translate(0,   0)   rotate(0deg);   }
  4%         { transform: translate(-18px, -6px)  rotate(-2deg);  }
  8%         { transform: translate( 18px,  6px)  rotate( 2deg);  }
  12%        { transform: translate(-16px,  4px)  rotate(-1.5deg);}
  16%        { transform: translate( 16px, -4px)  rotate( 1.5deg);}
  20%        { transform: translate(-12px, -6px)  rotate(-1deg);  }
  24%        { transform: translate( 12px,  6px)  rotate( 1deg);  }
  28%        { transform: translate(-8px,   3px)  rotate(-0.5deg);}
  32%        { transform: translate(  8px, -3px)  rotate( 0.5deg);}
  36%        { transform: translate(-4px,   2px)  rotate(0deg);   }
  40%        { transform: translate(  4px, -2px)  rotate(0deg);   }
  44%        { transform: translate(0,   0)   rotate(0deg);   }
}
@keyframes instagram-glow {
  0%,  100% { color: #93D4DE; text-shadow: none; }
  50%        { color: #ffffff; text-shadow: 0 0 28px rgba(147,212,222,0.95), 0 0 56px rgba(147,212,222,0.45); }
}
@keyframes text-beat {
  0%,  100% { transform: scale(1); }
  50%        { transform: scale(1.07); }
}
`;

// ─── PIN Modal ────────────────────────────────────────────────────────────────

const ADMIN_PIN = '9999';

const PinModal: React.FC<{ onSuccess: () => void; onClose: () => void }> = ({ onSuccess, onClose }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);

  const handleKey = (digit: string) => {
    if (digit === 'del') { setPin(p => p.slice(0, -1)); setError(false); return; }
    const next = pin + digit;
    if (next.length > 4) return;
    setPin(next);
    if (next.length === 4) {
      if (next === ADMIN_PIN) { onSuccess(); }
      else { setError(true); setTimeout(() => { setPin(''); setError(false); }, 800); }
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#1a2740', borderRadius: '24px', padding: '60px 70px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px',
        border: '3px solid #5b9bd5', minWidth: '480px',
      }}>
        <div style={{ color: '#5b9bd5', fontSize: '40px', fontWeight: 'bold' }}>🔐 Acesso Admin</div>
        {/* PIN dots */}
        <div style={{ display: 'flex', gap: '20px' }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: error ? '#e74c3c' : pin.length > i ? '#5b9bd5' : '#2a3a50',
              border: '3px solid ' + (error ? '#e74c3c' : '#5b9bd5'),
              transition: 'all 0.15s',
            }} />
          ))}
        </div>
        {/* Numpad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {['1','2','3','4','5','6','7','8','9','','0','del'].map((k) => (
            <button key={k} onClick={() => k && handleKey(k)}
              style={{
                width: '120px', height: '120px', fontSize: '44px', fontWeight: 'bold',
                borderRadius: '16px', border: 'none', cursor: k ? 'pointer' : 'default',
                backgroundColor: k === 'del' ? '#c0392b' : k ? '#253a54' : 'transparent',
                color: '#fff', transition: 'background 0.1s',
              }}
            >{k === 'del' ? '⌫' : k}</button>
          ))}
        </div>
        {error && <div style={{ color: '#e74c3c', fontSize: '30px', fontWeight: 'bold' }}>PIN incorreto</div>}
        <button onClick={onClose} style={{ color: '#aab', background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', marginTop: '-10px' }}>Cancelar</button>
      </div>
    </div>
  );
};

// ─── Softys Welcome Screen ────────────────────────────────────────────────────

const SoftysWelcome: React.FC<{ onContinue: () => void }> = ({ onContinue }) => {
  const navigate   = useNavigate();
  const [showPin, setShowPin] = useState(false);

  // Animation config (read once on mount — changes via admin panel take effect on next render)
  const animConfig = useRef<AnimConfig>(loadAnimConfig());
  const cfg = animConfig.current;

  // Typewriter state
  const [displayText, setDisplayText] = useState(cfg.typewriter ? '' : WELCOME_TEXT);
  const [typingCycle, setTypingCycle] = useState(0); // incremented every 60s to restart
  const [logoCycle,   setLogoCycle]   = useState(0);

  // ── QR pulse state (key change restarts CSS animation) ───────────────────────
  const [qrCycle, setQrCycle] = useState(0);

  // Ref holds QR timers — no React state in the scheduling path (avoids timing issues)
  const qrTimersRef = useRef<{
    first?: ReturnType<typeof setTimeout>;
    recurring?: ReturnType<typeof setInterval>;
  }>({});

  // ── Inject CSS keyframes once ─────────────────────────────────────────────────
  useEffect(() => {
    const id = 'softys-keyframes';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = KEYFRAMES_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // ── Typewriter + logo cycle: restart every 60 s ───────────────────────────────
  useEffect(() => {
    const t = setInterval(() => {
      setTypingCycle(c => c + 1);
      setLogoCycle(c  => c + 1);
    }, 60000);
    return () => clearInterval(t);
  }, []);

  // ── Typewriter effect + QR scheduling (tudo no mesmo efeito, sem estado intermediário) ──
  useEffect(() => {
    // Cancela quaisquer timers de QR do ciclo anterior ANTES de começar a digitar
    clearTimeout(qrTimersRef.current.first);
    clearInterval(qrTimersRef.current.recurring);
    qrTimersRef.current = {};

    if (!cfg.typewriter) {
      setDisplayText(WELCOME_TEXT);
      // Sem animação: agenda QR 3 s após a tela surgir
      if (cfg.qrPulse) {
        qrTimersRef.current.first     = setTimeout(() => setQrCycle(c => c + 1), 3000);
        qrTimersRef.current.recurring = setInterval(() => setQrCycle(c => c + 1), 15000);
      }
      return () => {
        clearTimeout(qrTimersRef.current.first);
        clearInterval(qrTimersRef.current.recurring);
      };
    }

    setDisplayText('');
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayText(WELCOME_TEXT.slice(0, i));
      if (i >= WELCOME_TEXT.length) {
        clearInterval(iv);
        // QR só é agendado AQUI — após o último caractere ser escrito de fato
        if (cfg.qrPulse) {
          qrTimersRef.current.first     = setTimeout(() => setQrCycle(c => c + 1), 2000);
          qrTimersRef.current.recurring = setInterval(() => setQrCycle(c => c + 1), 15000);
        }
      }
    }, 55); // ~55 ms/char ≈ 2.7 s total

    return () => {
      clearInterval(iv);
      clearTimeout(qrTimersRef.current.first);
      clearInterval(qrTimersRef.current.recurring);
    };
  }, [typingCycle, cfg.typewriter, cfg.qrPulse]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      onClick={onContinue}
      className="page-enter"
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#5b9bd5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: '60px 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Logo Softys (left-to-right reveal) ── */}
      <img
        key={`logo-${logoCycle}`}
        src="/assets/softys/logo_softys.png"
        alt="Softys"
        style={{
          maxWidth: '720px',
          width: '85%',
          marginBottom: '80px',
          objectFit: 'contain',
          ...(cfg.logoReveal
            ? { animation: 'logo-reveal 1.6s cubic-bezier(0.22,1,0.36,1) forwards' }
            : {}),
        }}
      />

      {/* ── Welcome text — typewriter with blinking cursor ── */}
      <div style={{
        color: '#FFFFFF',
        fontSize: '88px',
        fontWeight: 'bold',
        lineHeight: 1.3,
        maxWidth: '960px',
        marginBottom: '100px',
        minHeight: '340px', // prevent layout shift while typing
      }}>
        {displayText}
        {/* Blinking cursor: always visible — solid while typing, blinking after */}
        {cfg.typewriter && (
          <span
            style={{
              display: 'inline-block',
              width: '5px',
              height: '80px',
              backgroundColor: '#93D4DE',
              marginLeft: '10px',
              verticalAlign: 'middle',
              borderRadius: '2px',
              animation: 'blink-cursor 0.9s step-end infinite',
            }}
          />
        )}
      </div>

      {/* ── Instagram block ── */}
      <div style={{ marginBottom: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            color: '#93D4DE',
            fontSize: '52px',
            marginBottom: '30px',
            ...(cfg.textEffects
              ? { animation: 'instagram-glow 2.8s ease-in-out infinite' }
              : {}),
          }}
        >
          Siga no Instagram →
        </div>

        {/* QR Code — key changes every 15s to restart CSS animation */}
        <img
          key={`qr-${qrCycle}`}
          src="/assets/softys/qrcode_instagram.png"
          alt="QR Code Instagram"
          style={{
            width: '540px',
            height: '540px',
            objectFit: 'contain',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            padding: '16px',
            ...(cfg.qrPulse
              ? { animation: 'qr-shake 0.6s ease-in-out forwards' }
              : {}),
          }}
        />
      </div>

      {/* ── Toque para participar ── */}
      <div
        style={{
          color: '#93D4DE',
          fontSize: '64px',
          fontWeight: 'bold',
          marginTop: '-20px',
          ...(cfg.textEffects
            ? { animation: 'text-beat 1.8s ease-in-out infinite' }
            : {}),
        }}
      >
        Toque para participar
      </div>

      {/* PIN Modal */}
      {showPin && (
        <PinModal
          onSuccess={() => { setShowPin(false); navigate('/admin'); }}
          onClose={() => setShowPin(false)}
        />
      )}

      {/* Gear button — bottom right */}
      <button
        onClick={e => { e.stopPropagation(); setShowPin(true); }}
        style={{
          position: 'fixed', bottom: '96px', right: '36px',
          width: '90px', height: '90px', borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.35)', border: '2px solid rgba(255,255,255,0.3)',
          fontSize: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'rgba(255,255,255,0.6)', zIndex: 100,
        }}
        title="Admin"
      >⚙️</button>
    </div>
  );
};

// ─── Presentation Component ───────────────────────────────────────────────────

const Presentation: React.FC = () => {
  const navigate = useNavigate();
  const roles = useContext(RolesContext);
  const [presentationImage, setPresentationImage] = useState(false);
  const [presentationVideo, setPresentationVideo] = useState(false);
  const [endVideo, setEndVideo] = useState(false);

  function handlePresentationContinue() {
    if (endVideo) {
      navigate('/register')
      return
    }
    if (roles?.roles?.presentation_image === 'SOFTYS_WELCOME') {
      navigate('/register')
    } else if (roles?.roles?.presentation_image && !presentationImage) {
      setPresentationImage(true)
    } else if (roles?.roles?.presentation_video && !presentationVideo) {
      // setPresentationVideo(true)
    } else {
      navigate('/register')
    }
  }

  const HandleRegister = () => {
    if (endVideo) {
      navigate('/register')
    }
    return (<></>)
  }

  const handleVideoEnded = () => {
    setEndVideo(true)
  };

  useEffect(() => {
    const videoElement = document.getElementById('video-player') as HTMLVideoElement;
    videoElement?.addEventListener('ended', handleVideoEnded);
    return () => {
      videoElement?.removeEventListener('ended', handleVideoEnded);
    };
  }, []);

  useEffect(() => {
    if (roles?.roles?.presentation_video && !presentationVideo) {
      const timeoutId = setTimeout(() => setEndVideo(true), 42000);
      return () => clearTimeout(timeoutId);
    }
  }, [presentationImage]);

  // ── Softys custom welcome screen ──────────────────────────────────────────
  if (roles?.roles?.presentation_image === 'SOFTYS_WELCOME') {
    return (
      <AppContainer>
        <SoftysWelcome onContinue={handlePresentationContinue} />
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      {roles?.roles?.presentation_image && !presentationImage ? (
        <AppContainerImage onClick={handlePresentationContinue} />
      ) : roles?.roles?.presentation_video && !presentationVideo ? (
        <AppContainerVideo onClick={handlePresentationContinue} image={endVideo ? '' : ''}>
          <video
            autoPlay
            style={{ width: '100%', height: '70vh' }}
          >
            <source src={require('../../assets/redemac/video.mp4')} type="video/mp4" />
            Seu navegador não suporta o elemento de vídeo.
          </video>
        </AppContainerVideo>
      ) : (
        <HandleRegister />
      )}
    </AppContainer>
  );
};

export default Presentation;